<!-- BaseModal.vue -->
<template>
  <transition name="modal-fade">
    <div
      v-if="isOpen"
      class="modal-overlay"
      :class="{ 'modal-overlay--fullscreen': fullScreen }"
      @click.self="emit('close')"
    >
      <div
        class="modal-box"
        :class="{
          'modal-box--fullscreen': fullScreen,
          'modal-box--flush-content': flushBody,
        }"
      >
        <div class="modal-header">
          <h3 class="modal-title">
            {{ title }}
          </h3>
          <button
            class="close-button"
            @click="emit('close')"
          >
            ×
          </button>
        </div>
        <div
          class="modal-body"
          :class="{ 'modal-body--flush': flushBody }"
        >
          <slot />
        </div>
        <div
          v-if="!hideFooter"
          class="modal-footer"
        >
          <button
            class="close-button-bottom"
            @click="emit('close')"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>
  
  <script setup lang="ts">
  import { defineProps, defineEmits, watch, onUnmounted, nextTick } from 'vue';
  import { iframeCommunication } from '@/utils/iframeCommunication';
  
  const props = defineProps<{
    isOpen: boolean
    title?: string
    fullScreen?: boolean
    hideFooter?: boolean
    /** No modal body padding; slot provides full-bleed background (e.g. wager details) */
    flushBody?: boolean
  }>()
  
  const emit = defineEmits(['close'])
  
  const isInIframe = iframeCommunication.isRunningInIframe()
  let savedScrollPosition = 0
  
  // Prevent body scrolling when modal is open
  const preventBodyScroll = () => {
    if (isInIframe) {
      document.body.classList.add('modal-open')
      document.documentElement.classList.add('modal-open')
      return
    }
    savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollPosition}px`
    document.body.style.width = '100%'
  }
  
  const restoreBodyScroll = () => {
    if (isInIframe) {
      document.body.classList.remove('modal-open')
      document.documentElement.classList.remove('modal-open')
      return
    }
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo({
      top: savedScrollPosition,
      behavior: 'auto'
    })
  }
  
  const resetModalScroll = () => {
    nextTick(() => {
      document.querySelectorAll('.modal-body').forEach((node) => {
        if (node instanceof HTMLElement) {
          node.scrollTop = 0
        }
      })
    })
  }

  watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
      if (isInIframe) {
        iframeCommunication.sendScrollToTop()
      }
      preventBodyScroll()
      resetModalScroll()
    } else {
      restoreBodyScroll()
    }
  })
  
  onUnmounted(() => {
    restoreBodyScroll()
  })
  </script>

  <script lang="ts">
  export default {
    name: 'BaseModal',
  }
  </script>
  
  <style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--shadow-color);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 9999;
    overflow-y: auto;
  }

  .modal-overlay--fullscreen {
    align-items: stretch;
    padding: 0;
  }
  
  .modal-box {
    background-color: var(--colour-surface-cont-lowest);
    width: 100%;
    max-width: 1280px;
    min-width: 320px;
    padding: 0; /* Removed padding */
    position: relative;
    color: var(--colour-base-text-colour);
    min-height: auto;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    box-sizing: border-box;
    border-radius: var(--wagers-modal-radius, 8px);
    overflow: hidden;
  }

  .modal-box--fullscreen {
    max-width: none;
    width: 100%;
    min-height: 100vh;
    max-height: 100vh;
    height: 100%;
    border-radius: 0;
    margin: 0;
  }

  .modal-box--fullscreen .modal-header {
    border-radius: 0;
  }

  /* Iframe: fullscreen still fills the visible frame from the top */
  :global(body.iframe-mode) .modal-box--fullscreen {
    min-height: 100% !important;
    max-height: 100% !important;
    height: 100% !important;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    background: var(--colour-surface-cont-low);
    color: var(--text-on-dark-surface, var(--colour-text-inverse));
    border-bottom: 1px solid var(--colour-surface-cont);
    border-radius: var(--wagers-modal-header-radius, 8px 8px 0 0);
    margin-bottom: 0;
    flex-shrink: 0;
  }
  
  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: var(--wagers-modal-body-padding, 24px);
    background: var(--colour-surface-cont-lowest);
  }

  .modal-box--flush-content {
    background-color: transparent;
  }

  .modal-body--flush {
    padding: 0;
    background: transparent;
  }
  
  .modal-footer {
    display: flex;
    justify-content: center;
    padding: 16px;
    margin-top: 0;
    background: var(--colour-surface-cont-lowest);
    border-top: 1px solid var(--colour-surface-cont);
    flex-shrink: 0;
    border-radius: 0 0 8px 8px;
  }
  
  .modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: inherit;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: inherit;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
  }
  
  .close-button:hover {
    opacity: 1;
  }
  
  .close-button-bottom {
    background: var(--colour-orange-button);
    border: none;
    color: var(--text-button);
    padding: 12px 32px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, opacity 0.2s;
  }
  
  .close-button-bottom:hover {
    opacity: 0.9;
    background: var(--primary-hover);
  }
  
  .close-button-bottom:active {
    opacity: 0.8;
  }
  
  /* Mobile specific styles */
  @media (max-width: 768px) {
    .modal-overlay {
      align-items: flex-start;
      padding: 0;
    }
    
    .modal-box {
      min-height: 100vh;
      max-height: 100vh;
      border-radius: 0;
      padding: 0;
      max-width: 100%;
      margin: 0;
    }
    
    .modal-header {
      border-radius: 0;
    }

    .modal-body {
      padding: var(--wagers-modal-body-padding, 0);
      background: var(--colour-surface-cont-lowest);
    }
  }
  
  /* Desktop styles */
  @media (min-width: 769px) {
    .modal-overlay:not(.modal-overlay--fullscreen) {
      align-items: center;
      padding: 8px;
    }
    
    .modal-box:not(.modal-box--fullscreen) {
      min-height: auto;
      max-height: 90vh;
      border-radius: 8px;
      width: 100%;
      max-width: 1280px;
      padding: 8px;
    }
  }

  :global(body.iframe-mode) .modal-box:not(.modal-box--fullscreen) {
    margin: 0 auto !important;
    height: auto !important;
    min-height: 0 !important;
  }
  </style>
  
